import React from 'react';

/*
 * Line icons for the services step, in place of the boards' emoji (Lena,
 * 11 Oct: "i don't want emoji, use icons"). The same 24-unit stroke style as
 * the pet sheet and Review and book.
 */
type P = { size?: number; className?: string; strokeWidth?: number };

const Svg: React.FC<P & { children: React.ReactNode }> = ({ size = 16, className, strokeWidth = 2, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
    style={{ display: 'inline-block', flexShrink: 0 }}
  >
    {children}
  </svg>
);

export const PlusIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

/** Corners pointing out: a photo opens full screen. */
export const ExpandIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M15 4h5v5M9 20H4v-5M20 4l-6 6M4 20l6-6" />
  </Svg>
);

export const CheckIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Svg>
);

export const PencilIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M4 20h4L19 9l-4-4L4 16z" />
    <path d="M13.5 6.5l4 4" />
  </Svg>
);

export const RepeatIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
    <path d="M3 21v-5h5" />
  </Svg>
);

export const PackageIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
    <path d="M12 22V12M3.3 7l8.7 5 8.7-5M7.5 4.27l9 5.15" />
  </Svg>
);

export const StarIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
  </Svg>
);

export const ChevronDownIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

export const ChevronRightIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

export const CloseIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const PawIcon: React.FC<P> = p => (
  <Svg {...p}>
    <circle cx="5.5" cy="10" r="2" />
    <circle cx="9.5" cy="5.5" r="2" />
    <circle cx="14.5" cy="5.5" r="2" />
    <circle cx="18.5" cy="10" r="2" />
    <path d="M12 12c-3 0-5.5 3.2-5.5 5.5 0 1.7 1.3 2.5 2.8 2.5 1.2 0 1.8-.6 2.7-.6s1.5.6 2.7.6c1.5 0 2.8-.8 2.8-2.5C17.5 15.2 15 12 12 12z" />
  </Svg>
);

/** A flame: Popular (SF 63's 🔥). */
export const FlameIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M12 22c4 0 7-2.9 7-7 0-3.4-2.2-5.6-3.6-7.4-.4 1.6-1.2 2.7-2.4 3.4C13 7.6 11.6 4.6 9 2c.2 3-1.4 5.2-3 7.1C4.7 10.7 5 12.7 5 15c0 4.1 3 7 7 7z" />
    <path d="M12 22c-1.7 0-3-1.3-3-3 0-1.9 1.6-2.9 2.3-4.5.9 1 1.5 1.6 2.4 1.9.3-.6.5-1.2.5-1.9 1 .9 1.8 2.3 1.8 4.5 0 1.7-1.3 3-3 3z" />
  </Svg>
);

/** A rosette: "The salon's pick" (SF 63's ★; the star is the best match's). */
export const BadgeIcon: React.FC<P> = p => (
  <Svg {...p}>
    <circle cx="12" cy="9" r="6" />
    <path d="M8.5 13.8L7 22l5-3 5 3-1.5-8.2" />
  </Svg>
);

export const TagIcon: React.FC<P> = p => (
  <Svg {...p}>
    <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
    <circle cx="7.5" cy="7.5" r="1.5" />
  </Svg>
);
